import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CORE_MODULES } from '../config/modules';
import { useUIStore } from './uiStore';

// Stable fallbacks for getEnabledModules / getPermissions. Returning a fresh
// array here would make `useAuthStore((s) => s.getX())` hand React a new
// snapshot on every render, tripping useSyncExternalStore's infinite-loop
// guard ("Maximum update depth exceeded").
const CORE_MODULES_FALLBACK: string[] = [...CORE_MODULES];
const EMPTY_PERMISSIONS: string[] = [];

interface UserInfo {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  language: string;
  // is_system_admin dropped in Sprint 4b (ADR-0001). Platform operator
  // concept lives entirely in platform_users / console.anoorehr.com; tenant
  // users are never "system admins." Any remaining read is a bug.
  must_change_password?: boolean;
  totp_enabled?: boolean;
  phone?: string;
  preferences?: Record<string, string>;
}

export type PlatformRole =
  | 'platform_owner'
  | 'platform_finance'
  | 'platform_support'
  | 'platform_readonly'
  | 'platform_marketing';

export interface ImpersonationState {
  session_id: string;
  admin_user_id: string;
  admin_email: string;
  admin_name: string;
  target_group_id: string;
  target_user_id: string;
  started_at: string;
  expires_at: string;
  consent_mode: string;
  reason: string;
  write_mode: boolean;
  elevated_at: string | null;
  is_admin_perspective: boolean;
}

interface CompanyInfo {
  company_id: string;
  company_name: string;
  company_colour: string;
  company_logo_url?: string | null;
  // Resolved effective theme (platform default → per-tenant override cascade),
  // computed by the backend and surfaced on /auth/me. Optional so sessions from
  // before this field shipped fall back to the brand-color + balanced defaults.
  theme?: {
    palette: string | null;
    style: 'minimal' | 'balanced' | 'soft';
    dark_default: boolean;
  };
  country_code?: string;
  base_currency?: string;
  role_name: string;
  is_group_level: boolean;
  // Backend-derived admin flag — canonical gate for admin-only UI
  // (ModuleConfigPage, CompanySettingsPage, etc.). True for Company /
  // Group / legacy System Administrator. Never gate on role_name strings.
  is_tenant_admin?: boolean;
  enabled_modules?: string[];
  permissions?: string[];
  // Console-managed feature flags, resolved effective values (company override →
  // platform default → env fallback). Surfaced on /auth/me so the app gates UI
  // on the same flags the backend gates behaviour on.
  feature_flags?: Record<string, boolean>;
  // RBAC v2 — field-level grants & row-level scopes resolved by the
  // backend per (user, company). Empty when PERMISSION_ENGINE_V2 is OFF.
  // Drives field-hiding and scope filters in usePermission.
  field_permissions?: Record<string, 'none' | 'read' | 'write'>;
  scopes?: string[];
  employment_status?: string | null;
  setup_completed?: boolean;
  // Group info (for onboarding shell, branding)
  group_id?: string | null;
  group_name?: string | null;
  group_logo_url?: string | null;
  hiring_authority?: string; // 'group' | 'subsidiary'
  settings_mode?: 'solo' | 'independent' | 'shared' | 'group';
  // RBAC v2 / billing ownership
  is_primary?: boolean;
  can_manage_billing?: boolean;
}

interface AuthState {
  user: UserInfo | null;
  companies: CompanyInfo[];
  activeCompanyId: string | null;
  platformRole: PlatformRole | null;
  impersonation: ImpersonationState | null;
  isAuthenticated: boolean;
  // Cookie-rehydration status. 'pending' until the one-time boot check
  // (App.tsx useSessionValidator) has resolved getMe() against the httpOnly
  // session cookie. Guards must render a loading state while 'pending' to
  // avoid bouncing a cookie-authenticated cold load to /login before the
  // store has been hydrated. Never persisted — always starts 'pending' on a
  // fresh page load so the boot check runs exactly once.
  authStatus: 'pending' | 'ready';

  setBootstrapped: () => void;

  setUser: (
    user: UserInfo,
    companies: CompanyInfo[],
    defaultCompanyId?: string,
    platformRole?: PlatformRole | null,
  ) => void;
  setActiveCompany: (companyId: string) => void;
  clearActiveCompany: () => void;
  setPlatformRole: (role: PlatformRole | null) => void;
  setImpersonation: (state: ImpersonationState | null) => void;
  updateCompanyModules: (companyId: string, enabledModules: string[]) => void;
  updateCompanyPermissions: (companyId: string, permissions: string[]) => void;
  updateCompanyTheme: (companyId: string, theme: CompanyInfo['theme']) => void;
  updateCompanyFeatureFlags: (companyId: string, featureFlags: Record<string, boolean>) => void;
  markSetupComplete: (companyId: string) => void;
  logout: () => void;
  getActiveCompany: () => CompanyInfo | undefined;
  currentCompany?: CompanyInfo | undefined;
  getEnabledModules: () => string[];
  getFeatureFlags: () => Record<string, boolean>;
  getPermissions: () => string[];
  getFieldPermissions: () => Record<string, 'none' | 'read' | 'write'>;
  getScopes: () => string[];
  getEmploymentStatus: () => string | null | undefined;
  isOnboarding: () => boolean;
  needsSetup: () => boolean;
  isPlatformAdmin: () => boolean;
  getCanManageBilling: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      companies: [],
      activeCompanyId: null,
      platformRole: null,
      impersonation: null,
      isAuthenticated: false,
      authStatus: 'pending',

      setBootstrapped: () => set({ authStatus: 'ready' }),

      setUser: (user, companies, defaultCompanyId, platformRole) => {
        set({
          user,
          companies,
          isAuthenticated: true,
          // A login (or cookie rehydration) is itself a completed bootstrap —
          // mark ready so guards stop showing the loading state immediately.
          authStatus: 'ready',
          // When the backend returns a null `defaultCompanyId` for a platform
          // admin, respect it — do NOT fall back to the first company, or the
          // platform admin would be silently dropped into a tenant's books.
          // Only non-platform users auto-default to their first company when
          // the server didn't pick one.
          activeCompanyId: defaultCompanyId
            ?? (platformRole ? null : companies[0]?.company_id ?? null),
          platformRole: platformRole ?? null,
        });
        // NAV-001: hydrate the sidebar nav-style from the server-side user
        // preference so the choice follows the user across reloads, re-logins,
        // and devices (localStorage alone is per-browser). Local-only set —
        // does NOT echo a PATCH back to the server (that would loop on login).
        const navPref = user?.preferences?.nav_style;
        if (navPref === 'flat' || navPref === 'grouped') {
          useUIStore.getState().setNavStyle(navPref);
        }
      },

      setActiveCompany: (companyId) => set({ activeCompanyId: companyId }),

      clearActiveCompany: () => set({ activeCompanyId: null }),

      setPlatformRole: (role) => set({ platformRole: role }),

      setImpersonation: (state) => set({ impersonation: state }),

      updateCompanyModules: (companyId, enabledModules) =>
        set((state) => ({
          companies: state.companies.map((c) =>
            c.company_id === companyId ? { ...c, enabled_modules: enabledModules } : c
          ),
        })),

      updateCompanyPermissions: (companyId, permissions) =>
        set((state) => ({
          companies: state.companies.map((c) =>
            c.company_id === companyId ? { ...c, permissions } : c
          ),
        })),

      // Live-apply a resolved theme refreshed from /auth/me (e.g. after a
      // platform admin changes the theme in the Console). Only writes when the
      // theme actually differs so the background revalidation doesn't churn the
      // store reference on every mount. A new object reference is created only
      // on a real change → App.tsx's getActiveCompany().theme selector re-runs
      // and the MUI theme rebuilds without a re-login.
      updateCompanyTheme: (companyId, theme) =>
        set((state) => {
          const current = state.companies.find((c) => c.company_id === companyId);
          if (!current) return {};
          const a = current.theme;
          const b = theme;
          const same =
            (!a && !b) ||
            (!!a &&
              !!b &&
              a.palette === b.palette &&
              a.style === b.style &&
              a.dark_default === b.dark_default);
          if (same) return {};
          return {
            companies: state.companies.map((c) =>
              c.company_id === companyId ? { ...c, theme } : c
            ),
          };
        }),

      // Live-apply feature flags refreshed from /auth/me (e.g. after a platform
      // admin toggles a company flag in the Console). Shallow-compares so the
      // background revalidation only churns the store on a real change.
      updateCompanyFeatureFlags: (companyId, featureFlags) =>
        set((state) => {
          const current = state.companies.find((c) => c.company_id === companyId);
          if (!current) return {};
          const a = current.feature_flags ?? {};
          const b = featureFlags ?? {};
          const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
          let same = true;
          for (const k of keys) {
            if (a[k] !== b[k]) { same = false; break; }
          }
          if (same) return {};
          return {
            companies: state.companies.map((c) =>
              c.company_id === companyId ? { ...c, feature_flags: featureFlags } : c
            ),
          };
        }),

      markSetupComplete: (companyId) =>
        set((state) => ({
          companies: state.companies.map((c) =>
            c.company_id === companyId ? { ...c, setup_completed: true } : c
          ),
        })),

      logout: () => {
        // Clear analytics identity so subsequent events aren't attributed
        // to the logged-out user (no-op if consent was never granted).
        import('@/lib/analytics').then(({ resetAnalytics }) => resetAnalytics());
        set({
          user: null,
          companies: [],
          activeCompanyId: null,
          platformRole: null,
          impersonation: null,
          isAuthenticated: false,
        });
      },

      getActiveCompany: () => {
        const state = get();
        return state.companies.find((c) => c.company_id === state.activeCompanyId);
      },

      get currentCompany() {
        const state = get();
        return state.companies.find((c) => c.company_id === state.activeCompanyId);
      },

      getEnabledModules: () => {
        const state = get();
        const company = state.companies.find((c) => c.company_id === state.activeCompanyId);
        return company?.enabled_modules ?? CORE_MODULES_FALLBACK;
      },

      getFeatureFlags: () => {
        const state = get();
        const company = state.companies.find((c) => c.company_id === state.activeCompanyId);
        return company?.feature_flags ?? {};
      },

      getPermissions: () => {
        const state = get();
        const company = state.companies.find((c) => c.company_id === state.activeCompanyId);
        return company?.permissions ?? EMPTY_PERMISSIONS;
      },

      // RBAC v2 — field-level grants for the active company.
      // Returns {} when the backend hasn't shipped any (legacy tenants
      // pre-PERMISSION_ENGINE_V2 flip), in which case usePermission.ts
      // falls back to legacy module-level checks.
      getFieldPermissions: () => {
        const state = get();
        const company = state.companies.find((c) => c.company_id === state.activeCompanyId);
        return company?.field_permissions ?? {};
      },

      getScopes: () => {
        const state = get();
        const company = state.companies.find((c) => c.company_id === state.activeCompanyId);
        return company?.scopes ?? [];
      },

      getEmploymentStatus: () => {
        const state = get();
        const company = state.companies.find((c) => c.company_id === state.activeCompanyId);
        return company?.employment_status;
      },

      isOnboarding: () => {
        const state = get();
        const company = state.companies.find((c) => c.company_id === state.activeCompanyId);
        return company?.employment_status === 'onboarding';
      },

      needsSetup: () => {
        const state = get();
        const company = state.companies.find((c) => c.company_id === state.activeCompanyId);
        return company?.setup_completed === false;
      },

      isPlatformAdmin: () => {
        const state = get();
        return state.platformRole !== null;
      },

      getCanManageBilling: () => {
        const state = get();
        const company = state.companies.find((c) => c.company_id === state.activeCompanyId);
        return company?.can_manage_billing ?? false;
      },
    }),
    {
      name: 'etg-auth',
      // authStatus is intentionally NOT persisted — it must always start
      // 'pending' on a fresh page load so the boot check in App.tsx runs once.
      // Everything else is persisted as before.
      partialize: (state) => ({
        user: state.user,
        companies: state.companies,
        activeCompanyId: state.activeCompanyId,
        platformRole: state.platformRole,
        impersonation: state.impersonation,
        isAuthenticated: state.isAuthenticated,
      }),
      storage: {
        getItem: (name: string) => {
          const value = sessionStorage.getItem(name);
          return value ? JSON.parse(value) : null;
        },
        setItem: (name: string, value) => {
          sessionStorage.setItem(name, typeof value === 'string' ? value : JSON.stringify(value));
        },
        removeItem: (name: string) => {
          sessionStorage.removeItem(name);
        },
      },
    }
  )
);
